/**
 * Editor for a ProgramDocument.
 *
 * Loads all generated sections plus the active DocumentGenerationJob (if
 * any) and polls both while a generation is in flight. Shows per-section
 * progress, a retry banner on failures, and an error pill on 409 when the
 * user tries to start a second generation while one is running.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Download, Loader2, PlayCircle, RefreshCw } from 'lucide-react'
import Button from '../../components/ui/Button'
import SectionEditor from './SectionEditor'
import {
  exportProgramDocumentUrl,
  fetchSkills,
  generateProgramDocument,
  getActiveGenerationJob,
  getProgramDocumentEffectiveTemplate,
  getProgramDocumentSections,
  listGenerationJobs,
  type DocumentGenerationJob,
  type ProgramDocumentSection,
  GenerationConflictError,
} from '../../components/document-templates/api'
import type { ResolvedSection, SkillManifest } from '../../components/document-templates/types'
import { tokenManager } from '../../lib/api'

const POLL_INTERVAL_MS = 2500

interface ProgramDocumentEditorProps {
  /** When embedded inside a module page, ids are passed as props (the route
   *  has no :programDocumentId) and the back-to-documents chrome is hidden. */
  programId?: string
  programDocumentId?: string
  embedded?: boolean
}

export default function ProgramDocumentEditor(props: ProgramDocumentEditorProps = {}) {
  const params = useParams<{ programId: string; programDocumentId: string }>()
  const programId = props.programId ?? params.programId
  const programDocumentId = props.programDocumentId ?? params.programDocumentId
  const embedded = props.embedded ?? false
  const [sections, setSections] = useState<ProgramDocumentSection[]>([])
  const [activeJob, setActiveJob] = useState<DocumentGenerationJob | null>(null)
  const [lastJob, setLastJob] = useState<DocumentGenerationJob | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showExport, setShowExport] = useState(false)
  // Effective template + skill manifests back the per-section "where this
  // content comes from" note. Both are static relative to a generation run,
  // so they load once per document rather than on the polling path.
  const [resolvedSections, setResolvedSections] = useState<Map<string, ResolvedSection> | null>(null)
  const [skillManifests, setSkillManifests] = useState<SkillManifest[] | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchAll = useCallback(async () => {
    if (!programDocumentId) return
    try {
      const [sectionsData, job, history] = await Promise.all([
        getProgramDocumentSections(programDocumentId),
        getActiveGenerationJob(programDocumentId),
        listGenerationJobs(programDocumentId),
      ])
      setSections(sectionsData)
      setActiveJob(job)
      // Most recent terminal job — powers the failure banner when there's
      // no active generation and the last run ended with problems.
      const terminal = history.find((j) => !j.is_active) || null
      setLastJob(terminal)
    } catch (e: any) {
      setError(e?.message || 'Failed to load document')
    } finally {
      setLoading(false)
    }
  }, [programDocumentId])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  // The source note is supplementary: if either request fails the document
  // still edits normally, just without the note.
  useEffect(() => {
    if (!programDocumentId) return
    let cancelled = false
    Promise.all([
      getProgramDocumentEffectiveTemplate(programDocumentId),
      fetchSkills(),
    ])
      .then(([template, skills]) => {
        if (cancelled) return
        // Joined on `section_key`, not the template_section id: a
        // ProgramDocumentSection stores the leaf resolved at generation time,
        // which a later cascade override can replace. The key is stable.
        setResolvedSections(new Map(template.sections.map((s) => [s.key, s])))
        setSkillManifests(skills)
      })
      .catch(() => {
        if (cancelled) return
        setResolvedSections(null)
        setSkillManifests(null)
      })
    return () => { cancelled = true }
  }, [programDocumentId])

  // Poll while a job is active.
  useEffect(() => {
    if (!activeJob) {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
      return
    }
    if (!pollRef.current) {
      pollRef.current = setInterval(fetchAll, POLL_INTERVAL_MS)
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [activeJob, fetchAll])

  const handleGenerateAll = async () => {
    if (!programDocumentId) return
    setError(null)
    try {
      const job = await generateProgramDocument(programDocumentId)
      setActiveJob(job)
      setLastJob(null)
    } catch (e: any) {
      if (e instanceof GenerationConflictError) {
        setError(e.message)
        if (e.activeJob) setActiveJob(e.activeJob)
      } else {
        setError(e?.message || 'Generation failed to start')
      }
    }
  }

  const handleRetryFailed = async () => {
    if (!programDocumentId || !lastJob || !lastJob.failed_section_keys.length) return
    setError(null)
    try {
      const job = await generateProgramDocument(programDocumentId, {
        section_keys: lastJob.failed_section_keys,
      })
      setActiveJob(job)
      setLastJob(null)
    } catch (e: any) {
      if (e instanceof GenerationConflictError) {
        setError(e.message)
        if (e.activeJob) setActiveJob(e.activeJob)
      } else {
        setError(e?.message || 'Retry failed to start')
      }
    }
  }

  const updateSectionInPlace = (updated: ProgramDocumentSection) => {
    setSections((prev) => prev.map((s) => (s.id === updated.id ? updated : s)))
  }

  const handleSectionRegenerateQueued = async () => {
    if (!programDocumentId) return
    // Re-fetch the active job so the polling loop picks it up.
    const job = await getActiveGenerationJob(programDocumentId)
    setActiveJob(job)
  }

  const handleExport = async (format: 'md' | 'docx' | 'pdf') => {
    if (!programDocumentId) return
    setShowExport(false)
    const url = exportProgramDocumentUrl(programDocumentId, format)
    const token = tokenManager.getAccessToken()
    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!resp.ok) {
        setError(`Export failed: HTTP ${resp.status}`)
        return
      }
      const blob = await resp.blob()
      const disposition = resp.headers.get('Content-Disposition') || ''
      const match = disposition.match(/filename="?([^";]+)"?/)
      const filename = match?.[1] || `program-document.${format}`
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(link.href)
    } catch (e: any) {
      setError(e?.message || 'Export failed')
    }
  }

  const hasContent = sections.length > 0 && sections.some((s) => s.content_markdown)
  const generating = Boolean(activeJob)
  const jobFailureBanner = !activeJob && lastJob && (lastJob.status === 'partial_failure' || lastJob.status === 'failed')

  return (
    <div className={embedded ? 'space-y-4' : 'max-w-5xl mx-auto p-6 space-y-4'}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {!embedded && (
            <Link to={`/programs/${programId}/setup/documents`} className="inline-flex items-center gap-1 hover:text-foreground">
              <ArrowLeft className="w-4 h-4" /> Back to documents
            </Link>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleGenerateAll} disabled={generating}>
            {generating ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : hasContent ? (
              <RefreshCw className="w-4 h-4 mr-1" />
            ) : (
              <PlayCircle className="w-4 h-4 mr-1" />
            )}
            {hasContent ? 'Regenerate all' : 'Generate'}
          </Button>
          <div className="relative">
            <Button variant="outline" size="sm" onClick={() => setShowExport((v) => !v)}>
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
            {showExport && (
              <div className="absolute right-0 mt-1 bg-popover border border-border rounded-md shadow-lg z-20 py-1 min-w-[120px]">
                {(['md', 'docx', 'pdf'] as const).map((fmt) => (
                  <button
                    key={fmt}
                    onClick={() => handleExport(fmt)}
                    className="block w-full px-3 py-1.5 text-left text-sm hover:bg-accent"
                  >
                    {fmt.toUpperCase()}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {activeJob && <ActiveJobCard job={activeJob} />}
      {jobFailureBanner && (
        <FailureBanner job={lastJob!} onRetry={handleRetryFailed} />
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive rounded-lg p-3 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : sections.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-lg p-8 text-center space-y-3">
          <div className="text-muted-foreground">
            {generating
              ? 'Generation is running — the first sections will appear here shortly.'
              : "This document hasn't been generated yet."}
          </div>
          {!generating && (
            <Button onClick={handleGenerateAll}>
              <PlayCircle className="w-4 h-4 mr-1" /> Generate
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {sections.map((s) => (
            <SectionEditor
              key={s.id}
              section={s}
              programDocumentId={programDocumentId!}
              resolvedSection={resolvedSections?.get(s.section_key) ?? null}
              skillManifests={skillManifests}
              onChange={updateSectionInPlace}
              onRegenerateQueued={handleSectionRegenerateQueued}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function ActiveJobCard({ job }: { job: DocumentGenerationJob }) {
  const done = job.completed_section_keys.length
  const total = job.total_sections || (job.requested_section_keys.length || 0)
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  const current = job.current_section_key || '…'
  const label = job.status === 'queued'
    ? 'Queued…'
    : total > 0
      ? `Generating ${done + 1} of ${total}: ${current}`
      : `Generating: ${current}`
  return (
    <div className="bg-card border border-border rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm text-foreground">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        <span>{label}</span>
        {total > 0 && (
          <span className="ml-auto text-xs text-muted-foreground">{pct}%</span>
        )}
      </div>
      <div className="h-1 bg-muted rounded overflow-hidden">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function FailureBanner({ job, onRetry }: { job: DocumentGenerationJob; onRetry: () => void }) {
  const failed = job.failed_section_keys
  const isTotal = job.status === 'failed'
  return (
    <div className="bg-amber-500/10 border border-amber-500/50 rounded-lg p-3 space-y-2">
      <div className="flex items-start gap-2 text-sm">
        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-medium text-foreground">
            {isTotal
              ? 'The last generation failed.'
              : `The last generation completed with ${failed.length} failed section${failed.length === 1 ? '' : 's'}.`}
          </div>
          {failed.length > 0 && (
            <div className="text-xs text-muted-foreground mt-0.5">
              Failed: {failed.join(', ')}
            </div>
          )}
          {job.error_summary && (
            <pre className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap font-mono">
              {job.error_summary}
            </pre>
          )}
        </div>
        {failed.length > 0 && (
          <Button size="sm" variant="outline" onClick={onRetry}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Retry failed
          </Button>
        )}
      </div>
    </div>
  )
}
